import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { GenericLoading } from 'components/ui/Loader';
import { DepotDdnForm } from './components/DepotDdnForm';
import { DepotErrorScreen } from './components/DepotErrorScreen';
import { DepotThanksScreen } from './components/DepotThanksScreen';
import { DepotUploadZone } from './components/DepotUploadZone';
import {
  DepotBusinessError,
  DepotErrorCode,
  authentifierDepot,
  confirmerDepot,
  deposerFichier,
  fetchDepotEtat,
  formatDepotErrorMessage,
  isTerminalDepotErrorCode,
  supprimerFichier,
  visualiserFichier
} from './api/depotSecuriseApi';
import type {
  DepotBrandingDto,
  DepotSecuriseEtatResponse,
  DepotSecuriseInfosResponse,
  DepotSecuriseStatus
} from './types';

/**
 * Machine à états de la page publique de dépôt sécurisé.
 *
 *   loading                  ── /etat ──>  ddn-prompt (status=Valide)
 *                                          error (Introuvable/Expire/Bloque)
 *   ddn-prompt + ddn submit  ── /authentifier ──>  depot (succès)
 *                                                  ddn-prompt avec error métier (échec)
 *                                                  error 'Bloque' (3e échec = TokenBlocked)
 *   depot + drop fichier     ── /deposer ──>  depot avec uploadedCount incrémenté
 *                                              error (TokenBlocked/Expired/Invalid)
 *   depot + confirmer        ── /confirmer ──>  confirmed (terminal)
 *
 * On stocke la DDN validée localement (state) parce que le back nous l'oblige
 * à chaque action — pas de session côté souscripteur.
 *
 * Côté codes d'erreur : le back expose `error.code` (ex. MEDICAL_FORM_*).
 * On switche dessus pour distinguer les erreurs récupérables (DDN incorrecte,
 * fichier rejeté) des erreurs terminales (token bloqué/expiré) qui basculent
 * la page sur l'écran d'erreur dédié.
 */
type PageState =
  | { kind: 'loading' }
  | {
      kind: 'error';
      status: Exclude<DepotSecuriseStatus, 'Valide'> | 'NetworkError';
      branding?: DepotBrandingDto;
    }
  | {
      kind: 'ddn-prompt';
      firstName?: string;
      expiresAt?: string;
      errorMessage?: string;
      isSubmitting: boolean;
      branding?: DepotBrandingDto;
    }
  | {
      kind: 'depot';
      ddn: string;
      infos: DepotSecuriseInfosResponse;
      isConfirming: boolean;
      confirmErrorMessage?: string;
      branding?: DepotBrandingDto;
    }
  | {
      kind: 'confirmed';
      uploadedCount: number;
      branding?: DepotBrandingDto;
    };

/**
 * Convertit un code d'erreur terminal en `status` de l'écran d'erreur.
 * Les 4 codes (TokenInvalid, TokenExpired, TokenBlocked, SubscriberNotFound)
 * correspondent à 3 écrans : Introuvable / Expire / Bloque.
 */
function mapTerminalCodeToErrorStatus(
  code: string
): Exclude<DepotSecuriseStatus, 'Valide'> {
  if (code === DepotErrorCode.TokenExpired) return 'Expire';
  if (code === DepotErrorCode.TokenBlocked) return 'Bloque';
  // TokenInvalid + SubscriberNotFound → "Introuvable" : du point de vue
  // utilisateur c'est indiscernable (le lien ne mène à rien d'utilisable).
  return 'Introuvable';
}

export const DepotSecuriseMedicalPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<PageState>({ kind: 'loading' });

  /** GET /etat au mount — c'est la seule chose qu'on fait avant d'avoir la DDN. */
  useEffect(() => {
    if (!token) {
      setState({ kind: 'error', status: 'Introuvable' });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const etat: DepotSecuriseEtatResponse = await fetchDepotEtat(token);
        if (cancelled) return;
        if (etat.status === 'Valide') {
          setState({
            kind: 'ddn-prompt',
            firstName: etat.firstName,
            expiresAt: etat.expiresAt,
            isSubmitting: false,
            branding: etat.branding
          });
        } else {
          // Sur un statut terminal, le back peut quand même renvoyer le
          // branding (lien expiré sur un courtier reconnu) → on le garde.
          setState({
            kind: 'error',
            status: etat.status,
            branding: etat.branding
          });
        }
      } catch (e) {
        if (cancelled) return;
        // /etat peut renvoyer une erreur métier (token invalide/expiré) si le
        // back décide de mapper là plutôt que dans le status. On gère les deux.
        if (e instanceof DepotBusinessError && isTerminalDepotErrorCode(e.code)) {
          setState({
            kind: 'error',
            status: mapTerminalCodeToErrorStatus(e.code)
          });
          return;
        }
        setState({ kind: 'error', status: 'NetworkError' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  /**
   * Submit du form DDN → /authentifier.
   * - Succès : transition vers `depot`.
   * - Échec terminal (TokenBlocked au 3e essai, TokenExpired, etc.) :
   *   bascule sur l'écran d'erreur dédié.
   * - Échec récupérable (DobIncorrect, DobRequired) : on reste sur le form
   *   avec le message back qui inclut le compteur de tentatives.
   */
  const onAuthenticate = useCallback(
    async (dateNaissance: string) => {
      if (!token) return;
      setState((s) =>
        s.kind === 'ddn-prompt'
          ? { ...s, isSubmitting: true, errorMessage: undefined }
          : s
      );
      try {
        const infos = await authentifierDepot(token, dateNaissance);
        const currentBranding =
          state.kind === 'ddn-prompt' ? state.branding : undefined;
        if (infos.isConfirmed) {
          // Déjà confirmé côté back → token mort, on affiche l'écran final
          // sans permettre de re-déposer.
          setState({
            kind: 'confirmed',
            uploadedCount: infos.uploadedCount,
            branding: currentBranding
          });
          return;
        }
        setState({
          kind: 'depot',
          ddn: dateNaissance,
          infos,
          isConfirming: false,
          branding: currentBranding
        });
      } catch (e) {
        const currentBranding =
          state.kind === 'ddn-prompt' ? state.branding : undefined;
        if (e instanceof DepotBusinessError && isTerminalDepotErrorCode(e.code)) {
          setState({
            kind: 'error',
            status: mapTerminalCodeToErrorStatus(e.code),
            branding: currentBranding
          });
          return;
        }
        const message =
          e instanceof DepotBusinessError
            ? formatDepotErrorMessage(e)
            : 'Une erreur est survenue. Veuillez réessayer.';
        setState((s) =>
          s.kind === 'ddn-prompt' || s.kind === 'loading'
            ? {
                kind: 'ddn-prompt',
                firstName:
                  s.kind === 'ddn-prompt' ? s.firstName : undefined,
                expiresAt:
                  s.kind === 'ddn-prompt' ? s.expiresAt : undefined,
                isSubmitting: false,
                errorMessage: message,
                branding:
                  s.kind === 'ddn-prompt' ? s.branding : undefined
              }
            : s
        );
      }
    },
    [token]
  );

  /**
   * Upload d'un fichier — appelle /deposer. La réponse contient la liste
   * fraîche `uploadedDocuments` qui devient la source de vérité (on n'a
   * plus besoin d'incrémenter un compteur local ni de tenir un état
   * miroir).
   *
   * On laisse l'erreur remonter à `DepotUploadZone` pour qu'elle l'affiche
   * dans la ligne du fichier concerné. SAUF cas terminal (token mort entre
   * deux dépôts) où on intercepte ici pour basculer toute la page sur
   * l'écran d'erreur dédié — on rethrow quand même pour que la ligne du
   * fichier ne reste pas bloquée en "Envoi en cours…".
   */
  const onUploadFile = useCallback(
    async (file: File): Promise<{ message?: string }> => {
      if (!token || state.kind !== 'depot') {
        throw new Error('Session expirée. Rechargez la page.');
      }
      const ddn = state.ddn;
      try {
        const result = await deposerFichier(token, ddn, file);
        setState((s) =>
          s.kind === 'depot'
            ? {
                ...s,
                infos: {
                  ...s.infos,
                  uploadedDocuments: result.uploadedDocuments,
                  uploadedCount: result.uploadedDocuments.length
                }
              }
            : s
        );
        // Pour le toast/feedback inline, on préfère le `notification.message`
        // s'il est là (plus précis), sinon on retombe sur le title.
        const message =
          result.notification?.message ?? result.notification?.title;
        return { message };
      } catch (e) {
        if (
          e instanceof DepotBusinessError &&
          isTerminalDepotErrorCode(e.code)
        ) {
          const terminalStatus = mapTerminalCodeToErrorStatus(e.code);
          setState((s) => ({
            kind: 'error',
            status: terminalStatus,
            branding: s.kind === 'depot' ? s.branding : undefined
          }));
        }
        throw e;
      }
    },
    [token, state]
  );

  /**
   * Suppression d'un fichier déjà uploadé. Même pattern que onUploadFile :
   * on rafraîchit la liste à partir de la réponse du back, on rethrow les
   * codes terminaux après bascule de la page.
   */
  const onDeleteFile = useCallback(
    async (storedFileId: string): Promise<void> => {
      if (!token || state.kind !== 'depot') {
        throw new Error('Session expirée. Rechargez la page.');
      }
      const ddn = state.ddn;
      try {
        const result = await supprimerFichier(token, ddn, storedFileId);
        setState((s) =>
          s.kind === 'depot'
            ? {
                ...s,
                infos: {
                  ...s.infos,
                  uploadedDocuments: result.uploadedDocuments,
                  uploadedCount: result.uploadedDocuments.length
                }
              }
            : s
        );
      } catch (e) {
        if (
          e instanceof DepotBusinessError &&
          isTerminalDepotErrorCode(e.code)
        ) {
          const terminalStatus = mapTerminalCodeToErrorStatus(e.code);
          setState((s) => ({
            kind: 'error',
            status: terminalStatus,
            branding: s.kind === 'depot' ? s.branding : undefined
          }));
        }
        throw e;
      }
    },
    [token, state]
  );

  /**
   * Prévisualisation d'un document — appelle /visualiser et renvoie le blob
   * brut au composant qui s'occupera de l'object URL. Mêmes garde-fous que
   * onUploadFile / onDeleteFile : codes terminaux → bascule écran erreur,
   * autres erreurs rethrown pour affichage dans la modale.
   */
  const onPreviewFile = useCallback(
    async (
      storedFileId: string
    ): Promise<{ blob: Blob; contentType: string }> => {
      if (!token || state.kind !== 'depot') {
        throw new Error('Session expirée. Rechargez la page.');
      }
      const ddn = state.ddn;
      try {
        return await visualiserFichier(token, ddn, storedFileId);
      } catch (e) {
        if (
          e instanceof DepotBusinessError &&
          isTerminalDepotErrorCode(e.code)
        ) {
          const terminalStatus = mapTerminalCodeToErrorStatus(e.code);
          setState((s) => ({
            kind: 'error',
            status: terminalStatus,
            branding: s.kind === 'depot' ? s.branding : undefined
          }));
        }
        throw e;
      }
    },
    [token, state]
  );

  /** Confirmation finale — token mort après. */
  const onConfirm = useCallback(async () => {
    if (!token || state.kind !== 'depot') return;
    const { ddn, infos, branding } = state;
    setState({ ...state, isConfirming: true, confirmErrorMessage: undefined });
    try {
      await confirmerDepot(token, ddn);
      setState({
        kind: 'confirmed',
        uploadedCount: infos.uploadedCount,
        branding
      });
    } catch (e) {
      if (e instanceof DepotBusinessError && isTerminalDepotErrorCode(e.code)) {
        setState({
          kind: 'error',
          status: mapTerminalCodeToErrorStatus(e.code),
          branding
        });
        return;
      }
      const message =
        e instanceof DepotBusinessError
          ? formatDepotErrorMessage(e)
          : 'Une erreur est survenue. Veuillez réessayer.';
      setState((s) =>
        s.kind === 'depot'
          ? { ...s, isConfirming: false, confirmErrorMessage: message }
          : s
      );
    }
  }, [token, state]);

  // --- Rendu en fonction de l'état ---

  if (state.kind === 'loading') {
    return <GenericLoading loading isFullPage />;
  }

  if (state.kind === 'error') {
    return <DepotErrorScreen kind={state.status} branding={state.branding} />;
  }

  if (state.kind === 'ddn-prompt') {
    return (
      <DepotDdnForm
        firstName={state.firstName}
        expiresAt={state.expiresAt}
        isSubmitting={state.isSubmitting}
        errorMessage={state.errorMessage}
        onSubmit={onAuthenticate}
        branding={state.branding}
      />
    );
  }

  if (state.kind === 'depot') {
    return (
      <DepotUploadZone
        infos={state.infos}
        isConfirming={state.isConfirming}
        confirmErrorMessage={state.confirmErrorMessage}
        onUploadFile={onUploadFile}
        onDeleteFile={onDeleteFile}
        onPreviewFile={onPreviewFile}
        onConfirm={onConfirm}
        branding={state.branding}
      />
    );
  }

  // confirmed
  return (
    <DepotThanksScreen
      uploadedCount={state.uploadedCount}
      branding={state.branding}
    />
  );
};

export default DepotSecuriseMedicalPage;
